import { useState, useCallback, useEffect } from "react";
import { collection, getDocs, query, doc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../../../../services/firebase";
import { COLLECTIONS } from "../../../../services/collections";
import { memberDataFromFirestore } from "../../../../models/member";
import { useTenant } from "../../../../hooks/useTenant";
import EmployeeService from "../../../../services/employee_service";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "../../../../services/audit_service";
import { toast } from "react-toastify";

/**
 * usePortfolio
 * Gerencia a lógica da página de Carteira de Clientes.
 * Layout Master-Detail: sidebar com employees, detail com membros do employee selecionado.
 */
export const usePortfolio = () => {
    const { idTenant, user } = useTenant();

    const [employees, setEmployees] = useState([]);
    const [members, setMembers] = useState([]);
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [isTransferring, setIsTransferring] = useState(false);
    const [transferModalOpen, setTransferModalOpen] = useState(false);

    // Carregar employees ao montar
    useEffect(() => {
        const loadEmployees = async () => {
            if (!idTenant) return;
            try {
                setLoading(true);
                const data = await EmployeeService.getAllByTenant(idTenant);
                setEmployees(data || []);
            } catch (error) {
                console.error("Erro ao carregar colaboradores:", error);
                toast.error("Erro ao carregar lista de colaboradores.");
            } finally {
                setLoading(false);
            }
        };
        loadEmployees();
    }, [idTenant]);

    // Buscar membros por consultor
    const fetchMembers = useCallback(async (employeeId) => {
        if (!idTenant || !employeeId) {
            setMembers([]);
            return;
        }
        try {
            setLoadingMembers(true);
            setSelectedMemberIds([]);

            const membersRef = collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS);
            const snapshot = await getDocs(query(membersRef));
            
            const filtered = [];
            snapshot.docs.forEach(docSnap => {
                const data = memberDataFromFirestore(docSnap.id, docSnap.data());
                if (data.consultant?.uid === employeeId) {
                    filtered.push(data);
                }
            });

            // Ordenar por nome
            filtered.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
            setMembers(filtered);
        } catch (error) {
            console.error("Erro ao buscar membros da carteira:", error);
            toast.error("Erro ao buscar clientes do consultor.");
        } finally {
            setLoadingMembers(false);
        }
    }, [idTenant]);

    // Toggle de seleção individual
    const toggleMemberSelection = useCallback((memberId) => {
        setSelectedMemberIds(prev =>
            prev.includes(memberId)
                ? prev.filter(id => id !== memberId)
                : [...prev, memberId]
        );
    }, []);

    // Selecionar/Deselecionar todos
    const toggleSelectAll = useCallback(() => {
        setSelectedMemberIds(prev =>
            prev.length === members.length
                ? []
                : members.map(m => m.id)
        );
    }, [members]);

    // Transferir membros selecionados para outro employee
    const transferMembers = useCallback(async (targetEmployeeId, currentEmployeeId) => {
        if (!idTenant || !targetEmployeeId || selectedMemberIds.length === 0) return;

        const targetEmployee = employees.find(e => e.id === targetEmployeeId);
        if (!targetEmployee) {
            toast.error("Colaborador de destino não encontrado.");
            return;
        }

        const targetName = targetEmployee.fullName || `${targetEmployee.firstName || ""} ${targetEmployee.lastName || ""}`.trim();
        const sourceEmployee = employees.find(e => e.id === currentEmployeeId);
        const sourceName = sourceEmployee?.fullName || "N/A";

        try {
            setIsTransferring(true);

            const updatePromises = selectedMemberIds.map(async (memberId) => {
                const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, memberId);
                
                await updateDoc(memberRef, {
                    consultant: {
                        uid: targetEmployeeId,
                        name: targetName,
                        photoUrl: targetEmployee.photoUrl || null
                    },
                    updatedAt: Timestamp.now(),
                    updatedBy: {
                        uid: user?.uid || "system",
                        name: user?.displayName || user?.fullName || "Sistema"
                    }
                });

                const memberData = members.find(m => m.id === memberId);
                AuditService.log({
                    idTenant,
                    user,
                    module: AUDIT_MODULES.MEMBERS,
                    action: AUDIT_ACTIONS.UPDATE,
                    entityId: memberId,
                    entityName: memberData?.fullName || memberId,
                    description: `Consultor do cliente "${memberData?.fullName}" transferido de "${sourceName}" para "${targetName}".`,
                    details: {
                        consultant: { from: sourceName, to: targetName }
                    }
                });
            });

            await Promise.all(updatePromises);

            toast.success(`${selectedMemberIds.length} cliente(s) transferido(s) para ${targetName}!`);
            setSelectedMemberIds([]);
            setTransferModalOpen(false);

            // Recarregar a listagem do employee atual
            await fetchMembers(currentEmployeeId);
        } catch (error) {
            console.error("Erro ao transferir membros:", error);
            toast.error("Erro ao transferir clientes. Tente novamente.");
        } finally {
            setIsTransferring(false);
        }
    }, [idTenant, selectedMemberIds, employees, user, members, fetchMembers]);

    return {
        employees,
        members,
        selectedMemberIds,
        loading,
        loadingMembers,
        isTransferring,
        transferModalOpen,
        setTransferModalOpen,
        actions: {
            fetchMembers,
            toggleMemberSelection,
            toggleSelectAll,
            transferMembers
        }
    };
};
